import { useState, useEffect, ReactNode, useCallback } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { defaultLocale, getLanguageFromLocale, type Locale } from './config';
import { getTranslations } from '@/services/platform/data/cms';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { flattenMessages, removeConflictingKeys, isNestedObject, sanitizeMessage } from './utils';

interface IntlProviderProps {
  children: ReactNode;
  locale: Locale;
  onLocaleChange?: (locale: Locale) => void;
}

/**
 * Loads the local JSON fallback for a given locale.
 */
async function loadLocalMessages(locale: string): Promise<Record<string, any>> {
  try {
    const messages = await import(`./locales/${locale}.json`);
    return messages.default ?? messages;
  } catch (error) {
    console.warn(`Failed to load local translations for ${locale}:`, error);
    // If the specific locale doesn't exist, try the default
    if (locale !== defaultLocale) {
      try {
        const fallback = await import(`./locales/${defaultLocale}.json`);
        return fallback.default ?? fallback;
      } catch {
        return {};
      }
    }
    return {};
  }
}

export function IntlProvider({ children, locale }: IntlProviderProps) {
  const [messages, setMessages] = useState<Record<string, string> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const loadMessages = useCallback(async (targetLocale: Locale) => {
    setIsLoading(true);
    try {
      // Layer 3: Load local JSON
      const localMessages = await loadLocalMessages(targetLocale);

      // Layers 1+2: Fetch from CMS
      let cmsMessages: Record<string, any> = {};
      try {
        let response;
        try {
          response = await getTranslations(targetLocale);
        } catch {
          const langCode = getLanguageFromLocale(targetLocale);
          response = await getTranslations(langCode);
        }

        if (response?.messages) {
          cmsMessages = response.messages;
        }
      } catch (error) {
        console.warn('Failed to load translations from CMS, falling back to local JSON:', error);
      }

      // Flatten both dictionaries
      const localFlat = isNestedObject(localMessages) ? flattenMessages(localMessages) : localMessages;
      const cmsFlat = isNestedObject(cmsMessages) ? flattenMessages(cmsMessages) : cmsMessages;

      // Strip CMS entries where value equals key (empty Umbraco values)
      const cmsFlatFiltered = Object.fromEntries(
        Object.entries(cmsFlat).filter(([key, value]) => value !== key)
      );

      // Merge: CMS wins, local fills gaps
      let flatMessages = { ...localFlat, ...cmsFlatFiltered };

      // Sanitize variables: convert {{var}} to {var} and decode HTML entities
      for (const key in flatMessages) {
        if (typeof flatMessages[key] === 'string') {
          flatMessages[key] = sanitizeMessage(flatMessages[key]);
        }
      }

      // Remove conflicting keys to ensure react-intl doesn't get confused
      flatMessages = removeConflictingKeys(flatMessages);

      setMessages(flatMessages);
    } catch (error) {
      console.error('Failed to load i18n messages:', error);
      setMessages({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(locale);
  }, [locale, loadMessages]);

  // Convert locale format for react-intl (e.g., 'en-us' -> 'en-US')
  const intlLocale = locale.includes('-')
    ? `${locale.split('-')[0]}-${locale.split('-')[1].toUpperCase()}`
    : locale;

  if (isLoading) {
    return <SpinLoading />;
  }

  return (
    <ReactIntlProvider
      locale={intlLocale}
      messages={messages || {}}
      defaultLocale={defaultLocale}
      onError={err => {
        // Suppress missing translation warnings in development
        if (err.code === 'MISSING_TRANSLATION') {
          console.warn('Missing translation:', err.message);
          return;
        }
        console.error(err);
      }}
    >
      {children}
    </ReactIntlProvider>
  );
}
